import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProblemDetails } from "../../generated-api/account";
import {
  Title,
  Wrapper,
  Description,
  SmallIcon,
  SmallWrapper,
  TraceIdBadge,
  ButtonWrapper,
  TraceIdBadgeLink,
} from "./ProblemDetail.styles";
import { Button, Tooltip, TooltipTrigger } from "@gds-react/core";
import LazyLottie from "../LazyLottie/LazyLottie";
import { useSize } from "../../hooks/useSize";
import { CloseOutlined } from "@gds-react/icons";
import { useKeyPressed } from "../../hooks/useKeyPressed";
import { datadogRum } from "@datadog/browser-rum";

// Thrown values reach this component either as a fetch error carrying a response body, or as an already-parsed ProblemDetails.
type ThrownError = ProblemDetails & {
  message?: string;
  response?: { json: () => Promise<ProblemDetails> };
};

interface Props {
  kind?: "default" | "small";
  error: ThrownError;
  active?: boolean;
  onClose?: () => void;
  resetErrorBoundary?: () => void;
}

const FALLBACK_ERROR_TITLE = "An error occured!";

const FALLBACK_ERROR_DESCRIPTION =
  "Oops! Something went wrong on our end. Please try again later. We apologize for the inconvenience.";

export default function CoreProblemDetail({
  kind = "default",
  error,
  active = true,
  onClose,
  resetErrorBoundary,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const { t, i18n } = useTranslation();
  const [errorObject, setErrorObject] = useState<ProblemDetails | null>(null);

  const ctrlPressed = useKeyPressed("b");

  const sessionId = datadogRum.getInternalContext()?.session_id;

  useEffect(() => {
    const resolve = async (response: NonNullable<ThrownError["response"]>) => {
      setErrorObject(await response.json());
    };

    if (error) {
      if (error.response) {
        resolve(error.response);
      } else {
        setErrorObject(error);
      }
    }
  }, [error]);

  const title = useMemo(() => {
    if (
      errorObject?.titleLocalizationKey &&
      i18n.exists(errorObject.titleLocalizationKey)
    ) {
      return t(errorObject.titleLocalizationKey);
    } else if (errorObject?.title) {
      return errorObject.title;
    }
    return FALLBACK_ERROR_TITLE;
  }, [errorObject, t]);

  const details = useMemo(() => {
    if (
      errorObject?.detailLocalizationKey &&
      i18n.exists(errorObject.detailLocalizationKey)
    ) {
      return t(errorObject.detailLocalizationKey);
    } else if (errorObject?.detail) {
      return errorObject.detail;
    } else if (error?.message) {
      return error.message;
    }
    return FALLBACK_ERROR_DESCRIPTION;
  }, [errorObject, t, error]);

  const openHelpLink = (link: string) => {
    window.open(link, "_blank");
  };

  const renderDefault = () => (
    <Wrapper role="alert" ref={ref}>
      {onClose && (
        <ButtonWrapper onClick={onClose} aria-label="Close problem detail">
          <CloseOutlined style={{ width: 18, height: 18 }} />
        </ButtonWrapper>
      )}
      <LazyLottie
        animation="errorAnimation"
        speed={0.5}
        width={size.width > 160 ? 160 : size.width - 40}
        height={size.width > 160 ? 160 : size.width - 40}
        options={{
          loop: false,
          rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
            clearCanvas: true,
          },
        }}
        show={active}
      />
      <Title>{title}</Title>
      <Description>{details}</Description>
      {errorObject?.traceId && !ctrlPressed && (
        <TraceIdBadge>Trace ID: {errorObject?.traceId}</TraceIdBadge>
      )}

      {sessionId && !ctrlPressed && (
        <TraceIdBadge>Session ID: {sessionId}</TraceIdBadge>
      )}

      {sessionId && ctrlPressed && (
        <TraceIdBadgeLink
          href={`https://cn-platform.datadoghq.eu/rum/explorer?query=${encodeURIComponent(
            `@session.id:${sessionId}`,
          )}`}
          target="_blank"
        >
          Session ID: {sessionId}
        </TraceIdBadgeLink>
      )}

      {errorObject?.traceId && ctrlPressed && (
        <TraceIdBadgeLink
          href={`https://cn-platform.datadoghq.eu/apm/trace/${errorObject?.traceId}`}
          target="_blank"
        >
          Trace ID: {errorObject?.traceId}
        </TraceIdBadgeLink>
      )}
      {errorObject?.type && (
        <Button
          onClick={() => openHelpLink(errorObject?.type as string)}
          kind="tertiary"
          style={{ marginBottom: 16 }}
        >
          {t("errorBoundary.wantToKnowMore")}
        </Button>
      )}
      {resetErrorBoundary && (
        <Button onClick={resetErrorBoundary}>
          {t("errorBoundary.refresh")}
        </Button>
      )}
    </Wrapper>
  );

  const renderSmall = () => (
    <SmallWrapper role="alert">
      <TooltipTrigger side="right">
        <div>
          <SmallIcon style={{ color: "#e22749ff", position: "relative" }} />
        </div>
        <Tooltip>{`${details} - Trace ID: ${errorObject?.traceId}`}</Tooltip>
      </TooltipTrigger>
    </SmallWrapper>
  );

  return (
    <>
      {kind === "default" && renderDefault()}
      {kind === "small" && renderSmall()}
    </>
  );
}
